import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router";
import api from "../../../api";
import {
  AttandanceStatus,
  LectureAttendanceForSection,
} from "../../../domains/LectureAttendance";
import { createError, HTTPErrors } from "../../../errors";
import SectionInterface from "../../../interfaces/SectionInterface";
import { cacheKeyOf as summaryCacheKeyOf } from "../LectureSessionSummary/useFetchLectureSession";
import { cacheKeyOf as attendanceCacheKeyOf } from "./useFetchLectureAttedances";

type Props = {
  section: SectionInterface;
  lectureSessionId: string;
  onError: () => void;
  onSuccess: () => void;
};

export type UpdateAttendanceActions =
  | { type: "forAttendance" }
  | {
      type: "forAbsence";
      param: {
        absenceReason: LectureAttendanceForSection["absenceReason"];
        absenceDescription: LectureAttendanceForSection["absenceDescription"];
      };
    }
  | {
      type: "forStaffShareNote";
      param: {
        staffShareNote: LectureAttendanceForSection["staffShareNote"];
      };
    };

type UpdateAttendanceParams = {
  targets: ReadonlyArray<LectureAttendanceForSection>;
  action: UpdateAttendanceActions;
};

// NOTE: レスポンスには他のプロパティも含まれるが、lectureSessionIdしか扱わないのでそれだけを型定義している
type UpdateAttendanceResponse =
  | {
      lectureAttendance: {
        lectureSessionId: string;
      };
    }
  | {
      lectureAttendances: ReadonlyArray<{
        lectureSessionId: string;
      }>;
    };

type MutationResult = {
  actionType: UpdateAttendanceActions["type"];
  response: UpdateAttendanceResponse;
};

export const useMutateAttendance = ({
  section,
  lectureSessionId,
  onError,
  onSuccess,
}: Props) => {
  const navigate = useNavigate();
  const client = useQueryClient();
  return useMutation<MutationResult, HTTPErrors, UpdateAttendanceParams>({
    mutationFn: async ({ targets, action }) => {
      const ids = targets.map((target) => target.id);
      const params = () => {
        switch (action.type) {
          case "forStaffShareNote": {
            return {
              lecture_attendance: {
                staff_share_note: action.param.staffShareNote,
              },
            };
          }
          case "forAbsence": {
            return {
              lecture_attendance_ids: ids,
              lecture_attendance: {
                attendance_status: AttandanceStatus.absence,
                absence_reason: action.param.absenceReason,
                absence_description: action.param.absenceDescription,
              },
            };
          }
          case "forAttendance": {
            return {
              lecture_attendance_ids: ids,
              lecture_attendance: {
                attendance_status: AttandanceStatus.presence,
              },
            };
          }
        }
      };
      const url =
        action.type === "forStaffShareNote"
          ? `${urlOf(section, lectureSessionId)}/${ids[0]}`
          : urlOf(section, lectureSessionId);

      const result = await api.patch(url, params());

      if (result.ok) {
        return {
          actionType: action.type,
          response: (await result.json()) as UpdateAttendanceResponse,
        };
      }
      throw await createError(result);
    },
    onError,
    onSuccess({ actionType, response }) {
      let newLectureSessionId: string;
      // actionTypeによってレスポンス形式が異なるので分岐
      if (actionType === "forStaffShareNote") {
        newLectureSessionId = (
          response as { lectureAttendance: { lectureSessionId: string } }
        ).lectureAttendance.lectureSessionId;
      } else {
        // どのlectureAttendancesをとってもlectureSessionIdは同じなので先頭をとる
        newLectureSessionId = (
          response as {
            lectureAttendances: ReadonlyArray<{ lectureSessionId: string }>;
          }
        ).lectureAttendances[0].lectureSessionId;
      }

      if (newLectureSessionId !== lectureSessionId) {
        // RSが分割されてlectureSessionIdが変わるときはURLが変わるので遷移させる
        onSuccess(); // モーダルが残らないように、先にonSuccessを呼んでモーダルを閉じる
        navigate(
          `/sections/${section.id}/lecture_sessions/${newLectureSessionId}/attendees`,
        );
      } else {
        // lectureSessionIdが変わらない場合は再取得
        const attendanceCacheKey = attendanceCacheKeyOf(
          section,
          newLectureSessionId,
        );
        client.invalidateQueries({ queryKey: attendanceCacheKey });
        const summaryCacheKey = summaryCacheKeyOf({
          sectionId: section.id,
          lectureSessionId: newLectureSessionId,
        });
        client.invalidateQueries({ queryKey: summaryCacheKey });
        onSuccess();
      }
    },
  });
};

const urlOf = (section: SectionInterface, lectureSessionId: string) =>
  `/api/v1/sections/${section.id}/lecture_sessions/${lectureSessionId}/lecture_attendances`;
